// src/Components/NewHome.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './NewHome.css';
import topImage from '../assets/newHome_topimage.jpg';
import vimeal from '../assets/vimeal.png';
import enoughu from '../assets/enoughu.png';
import study_music from '../assets/study_music.png';
import nebula from '../assets/nebula.png';
import bengle from '../assets/bengle.png';
import kioedu from '../assets/kioedu.png';

export default function NewHome() {
  const navigate = useNavigate();

  const whoRef = useRef();
  const whatRef = useRef();
  const visionRef = useRef();
  const metricsRef = useRef();

  const [whoVisible, setWhoVisible] = useState(false);
  const [whatVisible, setWhatVisible] = useState(false);
  const [visionVisible, setVisionVisible] = useState(false);
  const [metricsVisible, setMetricsVisible] = useState(false);
  const impactMetrics = [
    { end: 60, suffix: '+', label: 'enterprise agents in production' },
    { end: 15, suffix: '',  label: 'industry verticals served' },
    { end: 98, suffix: '%', label: 'average accuracy improvement over baseline workflows' },
  ];
  const [counts, setCounts] = useState([0,0,0]);

  useEffect(() => {
    const obsOptions = { threshold: 0.3 };
    const whoObs = new IntersectionObserver(([entry]) => entry.isIntersecting && setWhoVisible(true), obsOptions);
    const whatObs = new IntersectionObserver(([entry]) => entry.isIntersecting && setWhatVisible(true), obsOptions);
    const visionObs = new IntersectionObserver(([entry]) => entry.isIntersecting && setVisionVisible(true), obsOptions);
    const metricsObs = new IntersectionObserver(([entry]) => entry.isIntersecting && setMetricsVisible(true), obsOptions);
    if (whoRef.current) whoObs.observe(whoRef.current);
    if (whatRef.current) whatObs.observe(whatRef.current);
    if (visionRef.current) visionObs.observe(visionRef.current);
    if (metricsRef.current) metricsObs.observe(metricsRef.current);
    return () => {
      whoObs.disconnect();
      whatObs.disconnect();
      visionObs.disconnect();
      metricsObs.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!metricsVisible) return;
    impactMetrics.forEach((m, idx) => {
      let start = 0;
      const duration = 1000;
      const stepTime = Math.abs(Math.floor(duration / m.end));
      const timer = setInterval(() => {
        start += 1;
        setCounts(prev => { const next = [...prev]; next[idx] = start; return next; });
        if (start >= m.end) clearInterval(timer);
      }, stepTime);
    });
  }, [metricsVisible]);

  const services = [
    {
      title: 'Custom AI Agent Development',
      subtitle: 'Tailor-made models for unique business logic',
      benefits: [
        'Industry-specific architecture',
        'Performance-tuned pipelines',
        'Full IP transfer & compliance',
      ],
      direction: 'left'
    },
    {
      title: 'AI Advisory & Road-mapping',
      subtitle: 'Clear strategies from concept to deployment',
      benefits: [
        'Opportunity discovery workshops',
        'Data readiness audits',
        'ROI-driven implementation plans',
      ],
      direction: 'right'
    },
    {
      title: 'End-to-End Integration',
      subtitle: 'Hands-on build, test, and launch',
      benefits: [
        'Secure cloud or on-prem execution',
        'Seamless API & UI embedding',
        'Ongoing optimisation & MLOps',
      ],
      direction: 'top'
    },
    {
      title: 'Investment & Partnership Facilitation',
      subtitle: 'Scaling winners through capital and alliances',
      benefits: [
        'Investor introductions',
        'Technical due diligence packs',
        'Joint-venture structuring',
      ],
      direction: 'bottom'
    },
    {
      title: 'ARCANES Agent Marketplace (2026)',
      subtitle: 'Access a library of pre-trained, high-fidelity agents',
      benefits: [
        'One-click deployment',
        'Usage-based pricing',
        'Continuous upgrades',
      ],
      direction: 'left'
    },
  ];

  return (
    <div className="new-home">
      <section className="hero">
        <img src={topImage} alt="Hero" className="hero-bg" />
        <div className="hero-overlay" />
        <div className="hero-content">
          <div className="hero-left">
            <h1>Build Smarter Agents</h1>
            <h1>for Every Industry</h1>
            <h1>with ARCANES</h1>
          </div>
          <div className="hero-right">
            <p className="hero-text">Proven Across Sectors</p>
            <p className="hero-text">See What ARCANES Delivers</p>
            <button className="hero-cta" onClick={() => navigate('/b2b/use-cases')}>
              → View Use Cases
            </button>
          </div>
        </div>
      </section>

      <section ref={whoRef} className={`who-section animate-left ${whoVisible ? 'visible' : ''}`}>
        <h2>Who We Are</h2>
        <p>
          ARCANES is an AI–first platform dedicated to designing, building, and scaling <strong>domain-specialized AI Agents</strong>.
          From deep-tech manufacturers to healthcare startups and global e-commerce brands, we equip organisations with the
          intelligence, architecture, and guidance they need to operationalise AI—securely, reliably, and at speed.
        </p>
      </section>

      <section ref={whatRef} className="what-section">
        <h2>What We Do</h2>
        <div className="services-grid">
          {services.map((svc, i) => (
            <div
              key={i}
              className={`service-card animate-${svc.direction} ${whatVisible ? 'visible' : ''}`}
            >
              <h3>{svc.title}</h3>
              <p className="subtitle">{svc.subtitle}</p>
              <ul>
                {svc.benefits.map((b, idx) => <li key={idx}>{b}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section ref={visionRef} className={`vision-section animate-right ${visionVisible ? 'visible' : ''}`}>
        <blockquote>“AI Agents for Every Task, Everywhere.”</blockquote>
        <p>Our long-term mission is to host a best-in-class agent for every domain—from mental-health counselling to
           last-mile logistics—so companies of any size can unlock world-class AI within minutes.</p>
      </section>

      <section ref={metricsRef} className="metrics-section">
        {impactMetrics.map((m, idx) => (
          <div key={idx} className="metric">
            <span className="count">{counts[idx]}{m.suffix}</span>
            <span className="label">{m.label}</span>
          </div>
        ))}
      </section>

      <section className="clients-section">
        <h2>Trusted By</h2>
        <div className="logos">
          {[vimeal, enoughu, study_music, nebula, bengle, kioedu].map((logoSrc, idx) => (
            <img key={idx} src={logoSrc} alt={`Client ${idx+1}`} />
          ))}
        </div>
        <div className="testimonials">
          <blockquote>
            “ARCANES transformed our supply-chain visibility with ease.”
            <footer>— Operations Lead, Global Logistics Co.</footer>
          </blockquote>
          <blockquote>
            “We saw a 3× boost in developer productivity using their custom agents.”
            <footer>— CTO, FinTech Startup</footer>
          </blockquote>
        </div>
      </section>
    </div>
  );
}